import type { ClockRecord, ContractHistoryEntry, ContractHistoryType, WorkDay, WorkDayListQuery, WorkDayPay, WorkDaySortKey, WorkDayStatus } from "@shyne/shared/api";
import { daily_work_day_status, daily_work_log_type } from "@/models/daily-work.model";
import { individual_payment_status, subs_payment_status } from "@/models/appointment.model";
import type { DailyWorkDayJoin, DailyWorkLogJoin } from "@/repositories/daily-work.repository";
import type { DailyWorkExecutionJoin } from "@/repositories/daily-work-execution.repository";

export type WorkDayListFilters = {
  contract?: string;
  assignedToMe?: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  status?: string;
};

const STATUS_OF_WORK_DAY: Record<WorkDayStatus, daily_work_day_status> = {
  pending: daily_work_day_status.pending,
  assigned: daily_work_day_status.assigned,
  accepted: daily_work_day_status.accepted,
  in_progress: daily_work_day_status.inprogress,
  completed: daily_work_day_status.completed,
};

const WORK_DAY_STATUS_OF_ROW = Object.fromEntries(Object.entries(STATUS_OF_WORK_DAY).map(([status, row]) => [row, status])) as Record<string, WorkDayStatus>;

const SORT_KEY_OF_COLUMN: Record<string, WorkDaySortKey> = {
  work_date: "date",
  "daily_work_data.property.name": "property",
};

const LOG_TYPE_OF_HISTORY: Record<ContractHistoryType, daily_work_log_type> = {
  created: daily_work_log_type.create,
  updated: daily_work_log_type.update,
  deleted: daily_work_log_type.delete,
  assigned: daily_work_log_type.task_assigned,
  unassigned: daily_work_log_type.task_unassigned,
  on_site: daily_work_log_type.clock_in,
  off_site: daily_work_log_type.clock_out,
  task_completed: daily_work_log_type.task_completed,
  status_changed: daily_work_log_type.status_change,
};

const payStatus = (pay: WorkDayPay | undefined) => pay?.status ?? null;

export const toWorkDayRecord = (day: WorkDay): DailyWorkDayJoin => ({
  id: day.id,
  daily_work: day.contract.id,
  work_date: day.date,
  status: STATUS_OF_WORK_DAY[day.status],
  service: { id: day.service.id, name: day.service.name, parent_name: day.service.category ?? "" },
  tasks: day.tasks,
  assigned_user: day.assignment.worker && { id: day.assignment.worker.id, name: day.assignment.worker.name },
  assigned_org: day.assignment.subcontractor && { id: day.assignment.subcontractor.id, display_name: day.assignment.subcontractor.name },
  individual_payment: day.pay.worker?.rate ?? null,
  individual_payment_total: day.pay.worker?.total ?? null,
  individual_payment_status: payStatus(day.pay.worker) as individual_payment_status | null,
  individual_payment_source: day.pay.worker?.source ?? null,
  subs_payment: day.pay.subcontractor?.rate ?? null,
  subs_payment_total: day.pay.subcontractor?.total ?? null,
  subs_payment_status: payStatus(day.pay.subcontractor) as subs_payment_status | null,
  subs_payment_source: day.pay.subcontractor?.source ?? null,
  max_hours: day.maxHours,
  duration_label: day.durationLabel,
  instructions: day.instructions,
  recurring: day.recurring,
  schedule_detached_at: day.detachedFromScheduleAt,
  hours_worked: day.hours.worked,
  hours_source: day.hours.source,
  payout_review: day.hours.review,
  completed_at: day.completedAt,
  created_at: day.createdAt,
  updated_at: day.updatedAt,
  daily_work_data: {
    id: day.contract.id,
    vendor: { id: day.contract.vendor.id, display_name: day.contract.vendor.name },
    property: { id: day.contract.property.id, name: day.contract.property.name, address: day.contract.property.address, image: day.contract.property.image },
    start_date: day.contract.start,
    end_date: day.contract.end,
  },
});

const toExecutionRecord = (record: ClockRecord, day: WorkDay): DailyWorkExecutionJoin => ({
  id: record.id,
  clock_in: record.onSiteTime,
  clock_out: record.offSiteTime,
  tasks: (record.tasks ?? []).map((task) => ({ name: task.name, done: task.done, completed_at: task.completedAt })),
  notes: record.notes ?? null,
  created_at: record.createdAt,
  updated_at: record.updatedAt,
  daily_work_day: {
    id: day.id,
    work_date: day.date,
    tasks: day.tasks,
    service: { id: day.service.id, name: day.service.name, parent_name: day.service.category ?? "" },
    assigned_user: day.assignment.worker && { id: day.assignment.worker.id, name: day.assignment.worker.name },
    assigned_org: day.assignment.subcontractor && { id: day.assignment.subcontractor.id, display_name: day.assignment.subcontractor.name },
  },
});

export const clockRecordsOf = (day: WorkDay): DailyWorkExecutionJoin[] => (day.clockRecord === null ? [] : [toExecutionRecord(day.clockRecord, day)]);

function historyMeta(entry: ContractHistoryEntry): Record<string, unknown> | null {
  if (entry.periodInvoice) {
    const { invoiceId, amount, daysCompleted, hoursWorked } = entry.periodInvoice;
    return { kind: "period_invoice", invoice_id: invoiceId, amount, days_completed: daysCompleted, hours_worked: hoursWorked };
  }
  if (entry.periodInvoiceError !== undefined) return { kind: "period_invoice_error", error: entry.periodInvoiceError };
  return null;
}

export const toContractLogRecord = (contract: string, entry: ContractHistoryEntry): DailyWorkLogJoin => ({
  id: entry.id,
  daily_work: contract,
  type: entry.type === null ? null : LOG_TYPE_OF_HISTORY[entry.type],
  message: entry.message,
  meta_data: historyMeta(entry),
  user: entry.author && { id: entry.author.id, name: entry.author.name ?? "" },
  created_at: entry.at,
  updated_at: entry.at,
});

export type WorkDayPageRequest = { page: number; pageSize: number; sortKey?: string; sortOrder?: "asc" | "desc"; actingOrganization?: string };

export function toWorkDayListQuery(
  { contract, assignedToMe, startDate, endDate, search, status }: WorkDayListFilters,
  { page, pageSize, sortKey = "work_date", sortOrder = "asc", actingOrganization }: WorkDayPageRequest,
): Partial<WorkDayListQuery> {
  const searched = search?.trim();
  const workDayStatus = status ? WORK_DAY_STATUS_OF_ROW[status] : undefined;
  return {
    ...(actingOrganization ? { actingOrganization } : {}),
    ...(contract ? { contract } : {}),
    ...(assignedToMe ? { assignedToMe } : {}),
    ...(startDate ? { from: startDate } : {}),
    ...(endDate ? { to: endDate } : {}),
    ...(searched ? { search: searched } : {}),
    ...(workDayStatus ? { status: workDayStatus } : {}),
    sort: SORT_KEY_OF_COLUMN[sortKey] ?? "date",
    order: sortOrder,
    page,
    pageSize,
  };
}
